import {
  CouponInputFormik,
  DurationType,
  PauseInputFormik,
} from "@modules/boxSubscription/formik/boxSubscriptionFormik"
import { CouponEntity } from "@modules/common/couponObject/entities/couponObjectEntity"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { PauseEntity } from "@modules/common/pauseObject/entities/pauseObjectEntity"
import { PriceObjectDto } from "@modules/common/priceObject/gateway/priceObjectApiDto"
import { TrialSubscriptionEntity } from "@modules/common/trialSubscription/entities/trailSubscriptionEntity"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { LightInvoiceEntity } from "@modules/finances/entities/lightInvoiceEntity"
import { convertUserFromDto } from "@modules/profile/profileDtos"
import { OnOffProgramProgressionEntity } from "@modules/programs/entities/onOffProgramProgressionEntity"
import { ProgramSubscriberDto } from "@modules/programs/programSubscriber/gateway/programSubscriberDto"
import { ProgramSubscriptionStatus } from "@modules/programs/programTypes"
import { convertProgramPriceFromDto } from "@modules/programs/services/programDto"
import { HUImage } from "@modules/types/imageTypes"
import { colors } from "@modules/ui/styles/colors"
import { convertToEnum } from "@modules/utils/enum"
import dayjs from "dayjs"
import { Language } from "@modules/language/utils/languageUtils"
import { LightPromoCodeEntity } from "@modules/promoCode/entities/lightPromoCodeEntity"
import { ProgramProgressionStatus } from "@modules/programs/programOnOffTypes"
import { DefaultTheme } from "styled-components"

export type ProgramSubscriberState = Omit<
  ProgramSubscriberEntity,
  | "getProgramSubscriptionStatusInfos"
  | "getPause"
  | "canSubscriptionBePaused"
  | "canApplyCoupon"
  | "canDeleteSubscriber"
  | "getCoupon"
  | "getWordingCouponPriceAfterDiscount"
  | "getWordingCouponDate"
  | "getWordingTrialDate"
  | "getWordingPause"
  | "getWordingPrice"
  | "getProgramSubscriptionOnOffStatusInfos"
>

export class ProgramSubscriberEntity {
  id: string
  username: string
  firstname: string
  lastname: string
  avatar?: HUImage
  subscriptionStatus: ProgramSubscriptionStatus
  periodEndDate: Date | null
  subscriptionDate: Date
  paidPrice: PriceObjectDto | null
  referencePrice: PriceObjectDto | null
  usedPromoCode?: LightPromoCodeEntity
  coupon?: CouponEntity
  pause?: PauseEntity
  trial?: TrialSubscriptionEntity
  internalNote?: string
  email?: string
  nationality?: string
  onOffProgramProgression: OnOffProgramProgressionEntity | null
  lastInvoices: LightInvoiceEntity[] | null
  nextPayment: PriceObjectDto | null
  nextPaymentDate: Date | null

  private constructor(programSubscriber: ProgramSubscriberState) {
    this.id = programSubscriber.id
    this.username = programSubscriber.username
    this.firstname = programSubscriber.firstname
    this.lastname = programSubscriber.lastname
    this.avatar = programSubscriber.avatar
    this.subscriptionStatus = programSubscriber.subscriptionStatus
    this.periodEndDate = programSubscriber.periodEndDate
    this.subscriptionDate = programSubscriber.subscriptionDate
    this.paidPrice = programSubscriber.paidPrice
    this.referencePrice = programSubscriber.referencePrice
    this.usedPromoCode = programSubscriber.usedPromoCode
    this.coupon = programSubscriber.coupon
    this.pause = programSubscriber.pause
    this.trial = programSubscriber.trial
    this.internalNote = programSubscriber.internalNote
    this.email = programSubscriber.email
    this.nationality = programSubscriber.nationality
    this.onOffProgramProgression = programSubscriber.onOffProgramProgression
    this.lastInvoices = programSubscriber.lastInvoices
    this.nextPayment = programSubscriber.nextPayment
    this.nextPaymentDate = programSubscriber.nextPaymentDate
  }

  static fromItemDto(dto: ProgramSubscriberDto): ProgramSubscriberEntity {
    return new ProgramSubscriberEntity({
      ...convertUserFromDto(dto),
      subscriptionStatus: convertToEnum(
        ProgramSubscriptionStatus,
        dto.subscriptionStatus,
        ProgramSubscriptionStatus.INACTIVE,
      ),
      subscriptionDate: new Date(dto.subscriptionDate),
      periodEndDate: dto.periodEndDate ? new Date(dto.periodEndDate) : null,
      paidPrice: dto.paidPrice ? convertProgramPriceFromDto(dto.paidPrice, true) : null,
      referencePrice: dto.referencePrice ? convertProgramPriceFromDto(dto.referencePrice, true) : null,
      usedPromoCode: dto.usedPromoCode ? LightPromoCodeEntity.fromDto(dto.usedPromoCode) : undefined,
      coupon: dto.coupon ? CouponEntity.fromDto(dto.coupon) : undefined,
      pause: dto.pause ? PauseEntity.fromDto(dto.pause) : undefined,
      trial: dto.trial ? TrialSubscriptionEntity.fromDto(dto.trial) : undefined,
      email: dto.email ?? undefined,
      nationality: dto.nationality ?? undefined,
      internalNote: dto.internalNote ?? undefined,
      lastInvoices: dto.lastInvoices ? dto.lastInvoices.map((invoice) => LightInvoiceEntity.fromDto(invoice)) : null,
      nextPayment: dto.nextPayment ? convertProgramPriceFromDto(dto.nextPayment, true) : null,
      nextPaymentDate: dto.nextPaymentDate ? new Date(dto.nextPaymentDate) : null,
      onOffProgramProgression: dto.onOffProgramProgressionDto
        ? OnOffProgramProgressionEntity.fromDto(dto.onOffProgramProgressionDto)
        : null,
    })
  }

  getWordingCouponDate(): string {
    if (!this.coupon) return ""

    return this.coupon.getWordingDate()
  }

  // The coupon is calculated after calculating the promo code if there is one
  getWordingCouponPriceAfterDiscount(language: Language): string {
    if (!this.referencePrice || !this.coupon) {
      return ""
    }

    let price = this.referencePrice.value
    if (this.usedPromoCode) {
      price =
        this.usedPromoCode.type === CouponType.PERCENT
          ? price - (price * this.usedPromoCode.value) / 100
          : price - this.usedPromoCode.value
    }

    price =
      this.coupon.type === CouponType.PERCENT ? price - (price * this.coupon.value) / 100 : price - this.coupon.value

    return formatCurrency(price, this.referencePrice.currency, language)
  }

  getProgramSubscriptionOnOffStatusInfos(theme: DefaultTheme): { color: string; label: string; information?: string } {
    if (!this.onOffProgramProgression) {
      return { color: theme.error, label: word("program.subscribers.subscriptionDetails.status.inactive.label") }
    }

    switch (this.onOffProgramProgression.status) {
      case ProgramProgressionStatus.NOT_STARTED:
        return {
          color: theme.neutralColor400,
          label: word("program.subscribers.subscriptionDetails.status.not_started.label"),
        }
      case ProgramProgressionStatus.COMPLETED:
        return { color: theme.sucess, label: word("program.subscribers.subscriptionDetails.status.completed.label") }
      case ProgramProgressionStatus.STARTED:
        return {
          color: theme.sucess,
          label: word("program.subscribers.subscriptionDetails.status.started.label"),
          information: this.onOffProgramProgression.getWordingStarted(),
        }
      case ProgramProgressionStatus.STOPPED:
        return {
          color: theme.warning,
          label: word("program.subscribers.subscriptionDetails.status.stopped.label"),
          information: this.onOffProgramProgression.getWordingPause(),
        }
      default:
        return { color: theme.error, label: word("program.subscribers.subscriptionDetails.status.inactive.label") }
    }
  }

  getProgramSubscriptionStatusInfos(): { color: string; label: string; information?: string } {
    switch (this.subscriptionStatus) {
      case ProgramSubscriptionStatus.ACTIVE:
      case ProgramSubscriptionStatus.ACTIVE_BUNDLE:
        return { color: colors.green500, label: word("program.subscribers.subscriptionDetails.status.active.label") }
      case ProgramSubscriptionStatus.INACTIVE:
      case ProgramSubscriptionStatus.INACTIVE_BUNDLE:
        return {
          color: colors.red500,
          label: `${word("program.subscribers.subscriptionDetails.status.inactive.label")} - ${dayjs(this.periodEndDate).format("L")}`,
        }
      case ProgramSubscriptionStatus.PENDING_CANCELLATION:
        return {
          color: colors.yellow500,
          label: `${word("program.subscribers.subscriptionDetails.status.pending_cancellation.label")} - ${dayjs(this.periodEndDate).format("L")}`,
        }
      case ProgramSubscriptionStatus.PENDING_PAYMENT:
        return {
          color: colors.red500,
          label: word("program.subscribers.subscriptionDetails.status.pending_payment.label"),
        }
      case ProgramSubscriptionStatus.PAUSE:
        return {
          color: colors.yellow500,
          label: `${word("program.subscribers.subscriptionDetails.status.paused.label")}`,
          information: this.getWordingPause(),
        }
      case ProgramSubscriptionStatus.PAYMENT_IN_ERROR:
        return {
          color: colors.red500,
          label: word("program.subscribers.subscriptionDetails.status.payment_in_error.label"),
          information: word("program.subscribers.subscriptionDetails.status.payment_in_error.information"),
        }
      case ProgramSubscriptionStatus.TRIALING:
        return {
          color: colors.green500,
          label: word("program.subscribers.subscriptionDetails.status.trialing.label"),
          information: this.getWordingTrialDate(),
        }
      case ProgramSubscriptionStatus.TRIAL_PENDING_CANCELLATION:
        return {
          color: colors.yellow500,
          label: `${word("program.subscribers.subscriptionDetails.status.trial_pending_cancellation.label")} - ${dayjs(this.trial?.endDate).format("L")}`,
        }
      case ProgramSubscriptionStatus.PENDING_REQUEST:
        return {
          color: colors.red500,
          label: word("program.subscribers.subscriptionDetails.status.pending_request.label"),
        }
    }
  }

  getWordingTrialDate(): string {
    if (!this.trial) return ""

    const totalDays = dayjs(this.trial.endDate).diff(dayjs(this.trial.startDate), "days")
    const remainingDays = dayjs(this.trial.endDate).diff(dayjs(), "days")
    const daysUsed = totalDays - remainingDays

    return word("program.subscribers.subscriptionDetails.status.trialing.information", {
      daysUsed,
      totalDays,
      date: dayjs(this.trial.startDate).format("L"),
    })
  }

  getWordingPause(): string {
    if (!this.pause) return ""

    const pausedAt = dayjs(this.pause.pausedAt).format("L")

    if (!this.pause.resumeAt) {
      return word("program.subscribers.subscriptionDetails.status.paused.unlimited", {
        date: dayjs(this.pause.pausedAt).format("L"),
      })
    }

    const totalMonths = Math.ceil(dayjs(this.pause.resumeAt).diff(dayjs(this.pause.pausedAt), "month", true))
    const monthsUsed = dayjs(this.pause.pausedAt).diff(dayjs(), "month")
    const resumeAt = dayjs(this.pause.resumeAt).format("L")
    return word("program.subscribers.subscriptionDetails.status.paused.limited", {
      monthsUsed,
      totalMonths,
      pausedAt,
      resumeAt,
    })
  }

  getWordingPrice(): string {
    if (this.paidPrice === null && this.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE) {
      return word("program.price.free")
    }

    switch (this.subscriptionStatus) {
      case ProgramSubscriptionStatus.PENDING_CANCELLATION:
      case ProgramSubscriptionStatus.PAUSE:
      case ProgramSubscriptionStatus.TRIAL_PENDING_CANCELLATION:
      case ProgramSubscriptionStatus.PAYMENT_IN_ERROR:
        return word("program.price.noFuturePayment")
      case ProgramSubscriptionStatus.TRIALING:
        return word("program.price.free")
      default:
        return ""
    }
  }

  getPause(): PauseInputFormik {
    if (!this.pause) {
      return {
        durationType: DurationType.MONTHLY,
        months: 1,
      }
    }

    if (this.pause.resumeAt === null && this.pause.pausedAt) {
      return {
        durationType: DurationType.UNLIMITED,
        months: 0,
      }
    }

    const pausedAt = dayjs(this.pause.pausedAt)
    const resumeAt = dayjs(this.pause.resumeAt)
    const months = resumeAt.diff(pausedAt, "month")

    return {
      durationType: months > 0 ? DurationType.MONTHLY : DurationType.UNLIMITED,
      months: months > 0 ? months : 1,
    }
  }

  getCoupon(): CouponInputFormik {
    if (!this.coupon) {
      return {
        type: CouponType.PERCENT,
        value: 0,
        durationType: DurationType.MONTHLY,
        months: 0,
      }
    }
    return {
      type: this.coupon.type,
      value: this.coupon.value,
      durationType: this.coupon.duration ? DurationType.MONTHLY : DurationType.UNLIMITED,
      months: this.coupon.duration || null,
    }
  }

  canSubscriptionBePaused(): boolean {
    return (
      !!this.paidPrice &&
      !this.onOffProgramProgression &&
      (this.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE ||
        this.subscriptionStatus === ProgramSubscriptionStatus.PAUSE ||
        this.subscriptionStatus === ProgramSubscriptionStatus.PENDING_PAYMENT)
    )
  }

  canApplyCoupon(): boolean {
    return (
      !!this.paidPrice &&
      !this.onOffProgramProgression &&
      (this.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE ||
        this.subscriptionStatus === ProgramSubscriptionStatus.PAUSE ||
        this.subscriptionStatus === ProgramSubscriptionStatus.PENDING_PAYMENT ||
        this.subscriptionStatus === ProgramSubscriptionStatus.PAYMENT_IN_ERROR ||
        this.subscriptionStatus === ProgramSubscriptionStatus.TRIALING)
    )
  }

  canDeleteSubscriber(): boolean {
    if (this.onOffProgramProgression) {
      return false
    }
    return true
  }
}
